import React, { useContext, useEffect, useMemo, useRef } from "react";
import { Result, Table } from "antd";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

import PropTypes from "prop-types";
import { formatCurrency, monthNames } from "../utils";
import { useCalculateComponentHeightSizeHook } from "../../../hooks";
import { ClassificationsContext } from "../../../contexts/ClassificationsContext";
import { getAllKeys, processData, renderColumns } from "./utilsCashFlowTable";
import { SmileOutlined } from "@ant-design/icons";

export const CashFlowTable = ({ 
	data = [], 
	currency = "Q",
	originalCurrency = "",
	exchangeRate = 7.71, 
	isUSDActive = false,
	basePoints = {},
	bankAccounts = [],
	currentYear = 0,
	basePointsAllreadyConverted = false,
	header,
	onDataToSend = () => {},
}) => {
	const { classifications } = useContext(ClassificationsContext);
  
	if(currentYear <= 0 && data.length !== 0) {
		return <></>;
	}

	if(data.length === 0){
		return (
			<>
				{header}
				<Result
					icon={<SmileOutlined />}
					title={`No se encontraron registros para el año ${currentYear}`}
				/>
			</>
		);
	}
	
	// Eliminamos el estado tableData que está causando renders adicionales
	const { tableHeight } = useCalculateComponentHeightSizeHook({heightDifference: 400});
	const columns = useMemo(() => renderColumns(currency), [currency]);
	const prevExportDataRef = useRef([]);
	
	// Procesamos los datos una sola vez
	const processedData = useMemo(() => processData({
		dataToProcess: data,
		exchangeRate,
		isUSDActive,
		basePoints,
		bankAccounts,
		currentYear,
		originalCurrency,
		basePointsAllreadyConverted,
		classifications,
	}), [data, exchangeRate, isUSDActive, basePoints, bankAccounts, currentYear, originalCurrency, basePointsAllreadyConverted, classifications]);
	
	const {
		rows,
		grandTotal,
		initialAmountTotalRow,
		finalAmountTotalRow,
		detectedInitialAmountBankAccounts = [],
	} = processedData;
	
	// Generate export data usando los resultados ya procesados
	const exportData = useMemo(() => {
		// Verificamos si rows está vacío en lugar de tableData
		if (rows.length === 0) return [];
		
		const createMatrix = (rows, grandTotal, initialAmountTotalRow, finalAmountTotalRow, accountsData) => {
			const headerRow = ["Clasificación", ...monthNames.map((monthTitle) => monthTitle.slice(0, 3)), "Total"];
			
			const processRows = (rows, depth = 0) => {
				const matrix = [];
				rows.forEach(row => {
					const rowData = [
						`${" ".repeat(depth * 4)}${row.name}`,
						...monthNames.map(month => row[month] || 0),
						row.total || 0,
					];
					matrix.push(rowData);
					
					if (row.children && row.children.length > 0) {
						matrix.push(...processRows(row.children, depth + 1));
					}
				});
				return matrix;
			};
			
			const dataRows = processRows(rows).filter(row => row.length > 0);
			
			const initialValuesDetected = (accountsData || []).map(account => ([
				account?.name || "N/A",
				...(account?.monthly || Array(12).fill(0)),
				account?.total || 0,
			]));
			
			const totalRow = ["General(Neto):", ...grandTotal.monthly, grandTotal.total];
			const initialAmountRow = ["Saldo Inicial:", ...initialAmountTotalRow, ""];
			const finalAmountRow = ["Saldo Final:", ...finalAmountTotalRow, ""];
			
			if (!basePointsAllreadyConverted && initialValuesDetected.length === 0) {
				return [headerRow, ...dataRows, totalRow, initialAmountRow, finalAmountRow];
			}
			
			return [headerRow, ...dataRows, ...initialValuesDetected, totalRow, initialAmountRow, finalAmountRow];
		};
		
		return createMatrix(
			rows,
			grandTotal,
			initialAmountTotalRow,
			finalAmountTotalRow,
			detectedInitialAmountBankAccounts
		);
	}, [
		// Eliminamos tableData de las dependencias
		rows, 
		grandTotal, 
		initialAmountTotalRow, 
		finalAmountTotalRow, 
		detectedInitialAmountBankAccounts,
		basePointsAllreadyConverted
	]);
  
	// Usamos un useRef para detectar cambios sin causar re-renders
	const lastExportDataRef = useRef(null);
	
	useEffect(() => {
		// Solo enviamos datos si realmente cambiaron
		if (JSON.stringify(lastExportDataRef.current) !== JSON.stringify(exportData)) {
			lastExportDataRef.current = exportData;
			onDataToSend(exportData);
			prevExportDataRef.current = exportData;
		}
	}, [exportData, onDataToSend]);
	
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 5 },
		})
	);
  
	// Usamos rows directamente en lugar de tableData
	const allKeys = useMemo(() => getAllKeys(rows), [rows]);
  
	return (
		<DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]}>
			<SortableContext items={allKeys} strategy={verticalListSortingStrategy}>
				<Table
					rowKey="key"
					size="small"
					columns={columns}
					dataSource={rows}
					pagination={false}
					bordered
					scroll={{
						x: "max-content",
						y: tableHeight,
					}}
					expandable={{
						defaultExpandAllRows: true,
						expandRowByClick: false,
						showExpandColumn: false,
					}}
					title={() => (header)}
					summary={() => (
						<Table.Summary fixed>            
							{detectedInitialAmountBankAccounts.map((account, accountIndex) => (
								<Table.Summary.Row key={accountIndex}>
									<Table.Summary.Cell index={0} colSpan={1}> {account.name} </Table.Summary.Cell>
									{Array.from({ length: 12 }, (_, i) => (
										<Table.Summary.Cell index={i + 1} key={i} align="right">
											{account.monthly[i] !== 0 ? `${currency} ${formatCurrency(account.monthly[i])}` : "-"}
										</Table.Summary.Cell>
									))}
									<Table.Summary.Cell index={13} align="right">
										<strong>{currency} {formatCurrency(account.total)}</strong>
									</Table.Summary.Cell>
								</Table.Summary.Row>
							))}
							
							<Table.Summary.Row>
								<Table.Summary.Cell index={0} colSpan={1}>
									<strong>General (Neto):</strong>
								</Table.Summary.Cell>
								{Array.from({ length: 12 }, (_, i) => (
									<Table.Summary.Cell index={i + 1} key={i} align="right">
										<strong>{currency} {formatCurrency(grandTotal.monthly[i])}</strong>
									</Table.Summary.Cell>
								))}
								<Table.Summary.Cell index={13} align="right">
									<strong>{currency} {formatCurrency(grandTotal.total)}</strong>
								</Table.Summary.Cell>
							</Table.Summary.Row>
			
							<Table.Summary.Row>
								<Table.Summary.Cell index={0} colSpan={1}>
									<strong>Saldo Inicial:</strong>
								</Table.Summary.Cell>
								{Array.from({ length: 12 }, (_, i) => (
									<Table.Summary.Cell index={i + 1} key={i} align="right">
										<strong>{currency} {formatCurrency(initialAmountTotalRow[i])}</strong>
									</Table.Summary.Cell>
								))}
								<Table.Summary.Cell index={13} />
							</Table.Summary.Row>
								
							<Table.Summary.Row>
								<Table.Summary.Cell index={0} colSpan={1}>
									<strong>Saldo Final:</strong>
								</Table.Summary.Cell>
								{Array.from({ length: 12 }, (_, i) => (
									<Table.Summary.Cell index={i + 1} key={i} align="right">
										<strong>{currency} {formatCurrency(finalAmountTotalRow[i])}</strong>
									</Table.Summary.Cell>
								))}
								<Table.Summary.Cell index={13} />
							</Table.Summary.Row>
						</Table.Summary>
					)}
				/>
			</SortableContext>
		</DndContext>
	);
};

CashFlowTable.propTypes = {
	data: PropTypes.array,
	exchangeRate: PropTypes.number,
	isUSDActive: PropTypes.bool,
	currency: PropTypes.string,
	originalCurrency: PropTypes.string,
	basePoints: PropTypes.object,
	bankAccounts: PropTypes.array,
	currentYear: PropTypes.number,	
	basePointsAllreadyConverted: PropTypes.bool,
	initialAmount: PropTypes.number,
	onDataToSend: PropTypes.func,
	header: PropTypes.node,
};